/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors } from '../../../shared/theme/ticketColors'
import { ticketSurfaces } from '../../../shared/ticket/ticketSurfaces.styles';
import {
  formatOrderActivityEvent,
  formatOrderActivityTime,
  type OrderActivityEvent,
} from '@shared/orderActivity';

type Props = {
  events: OrderActivityEvent[];
};

export function OrderActivitySection({ events }: Props) {
  const [open, setOpen] = useState(false);

  if (!events.length) return null;

  const lines = events.map((event) => formatOrderActivityEvent(event, 'business'));
  const latest = lines[lines.length - 1];

  return (
    <View style={styles.card}>
      <Pressable
        onPress={() => setOpen((v) => !v)}
        style={styles.header}
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        accessibilityLabel="Actividad del pedido"
      >
        <View style={styles.headerText}>
          <Text style={styles.label}>Actividad del pedido</Text>
          {!open && latest ? (
            <Text style={styles.preview} numberOfLines={1}>
              {latest.title} · {formatOrderActivityTime(latest.at)}
            </Text>
          ) : (
            <Text style={styles.preview}>
              {lines.length} {lines.length === 1 ? 'evento' : 'eventos'}
            </Text>
          )}
        </View>
        <Ionicons
          name={open ? 'chevron-up' : 'chevron-down'}
          size={18}
          color={ticketColors.ink3}
        />
      </Pressable>

      {open
        ? lines.map((line, idx) => (
            <View key={line.id} style={[styles.row, idx === lines.length - 1 && styles.rowLast]}>
              <View style={styles.rail}>
                <View style={styles.dot} />
                {idx < lines.length - 1 ? <View style={styles.line} /> : null}
              </View>
              <View style={styles.body}>
                <View style={styles.head}>
                  <Text style={styles.title}>{line.title}</Text>
                  <Text style={styles.time}>{formatOrderActivityTime(line.at)}</Text>
                </View>
                {line.detail ? <Text style={styles.detail}>{line.detail}</Text> : null}
              </View>
            </View>
          ))
        : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    ...ticketSurfaces.paperSheet,
    paddingHorizontal: 16,
    paddingVertical: 4,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 12,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  label: {
    color: ticketColors.ink3,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  preview: {
    color: ticketColors.ink2,
    fontSize: 13,
    lineHeight: 18,
  },
  row: {
    flexDirection: 'row',
    gap: 12,
    paddingBottom: 14,
  },
  rowLast: {
    paddingBottom: 12,
  },
  rail: {
    width: 12,
    alignItems: 'center',
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: ticketColors.gold,
    marginTop: 4,
  },
  line: {
    flex: 1,
    width: 1,
    backgroundColor: ticketColors.rule,
    marginTop: 4,
  },
  body: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  head: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
    alignItems: 'flex-start',
  },
  title: {
    color: ticketColors.ink,
    fontSize: 14,
    fontWeight: '700',
    flex: 1,
  },
  time: {
    color: ticketColors.ink3,
    fontSize: 11,
    fontWeight: '500',
  },
  detail: {
    color: ticketColors.ink2,
    fontSize: 13,
    lineHeight: 18,
  },
});
